// =============================================
// A party's ledger: opening balance, activity, reconciliation  (migration 125)
// =============================================
// A journal line can name the vendor / customer / other party it is with
// (acctg_gl_journal_line.party_kind + party_id). The lines naming a party ARE its ledger:
//
//   opening balance   the party's lines on the newest OPENING-BALANCES entry
//                     (acctg_gl_journal.is_opening) of those books; when there is none,
//                     the figure typed in Masters (opening_balance, a record only);
//   activity          every later posted line naming the party;
//   reconciliation    lines ticked off against the party's statement carry the
//                     reconcile_id of that reconciliation (acctg_party_reconcile).
//
// Amounts are in the PARTY's terms — positive = the balance grows:
//   vendor / other party   credit − debit   (what the books owe them)
//   customer               debit − credit   (what they owe the books)
//
// Pure + unit-tested (partyLedger.test.ts).
// =============================================

export type LedgerPartyKind = 'vendor' | 'customer' | 'other';

export interface LedgerBooks { account_owner_id: number | string; owner_type: string; owner_id: number | string; }

export interface LedgerJournal {
  journal_id: number | string;
  account_owner_id?: number | string;
  owner_type?: string;
  owner_id?: number | string;
  journal_date?: string | null;
  journal_number?: string | null;
  memo?: string | null;
  status?: string;
  is_deleted?: number | string | null;
  is_opening?: number | string | null;
}

export interface LedgerLine {
  line_id: number | string;
  journal_id: number | string;
  debit?: number | string | null;
  credit?: number | string | null;
  line_memo?: string | null;
  party_kind?: string | null;
  party_id?: number | string | null;
  reconcile_id?: number | string | null;
  is_deleted?: number | string | null;
}

export interface LedgerRow {
  line_id: number;
  journal_id: number;
  date: string;
  ref: string;                  // JE-00012
  memo: string;
  amount: number;               // party terms; > 0 a charge, < 0 a payment
  balance: number;              // running, after this row
  reconcile_id: number | null;
}

export interface PartyLedger {
  opening: number;
  openingDate: string;          // '' when unknown
  openingFrom: 'journal' | 'manual' | 'none';
  openingRef: string;           // the opening entry's number, when from a journal
  rows: LedgerRow[];
  balance: number;              // opening + every row
}

const num = (v: any) => Number(v) || 0;
const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const day = (v: any) => String(v || '').slice(0, 10);

/** A debit/credit pair in the party's own terms (positive = its balance grows). */
export function partyAmount(kind: LedgerPartyKind, debit: any, credit: any): number {
  return round2(kind === 'customer' ? num(debit) - num(credit) : num(credit) - num(debit));
}

const inBooks = (j: LedgerJournal, books?: LedgerBooks | null) => !books ||
  // eslint-disable-next-line eqeqeq
  (j.account_owner_id == books.account_owner_id && j.owner_type === books.owner_type && j.owner_id == books.owner_id);

/** Posted lines naming the party (in those books, or any when none given), oldest first. */
function partyEntries(journals: LedgerJournal[], lines: LedgerLine[], kind: LedgerPartyKind, partyId: number | string, books?: LedgerBooks | null) {
  const byId = new Map<number, LedgerJournal>();
  journals.forEach(j => {
    // eslint-disable-next-line eqeqeq
    if (j.is_deleted != 1 && j.status === 'posted' && inBooks(j, books)) byId.set(Number(j.journal_id), j);
  });
  return lines
    // eslint-disable-next-line eqeqeq
    .filter(l => l.is_deleted != 1 && l.party_kind === kind && l.party_id != null && l.party_id == partyId && byId.has(Number(l.journal_id)))
    .map(l => ({ line: l, journal: byId.get(Number(l.journal_id))!, amount: partyAmount(kind, l.debit, l.credit) }))
    .sort((a, b) => day(a.journal.journal_date).localeCompare(day(b.journal.journal_date))
      || Number(a.journal.journal_id) - Number(b.journal.journal_id) || Number(a.line.line_id) - Number(b.line.line_id));
}

/**
 * The party's opening balance as an opening-balances journal entry states it: the
 * party's lines on the NEWEST such entry. null when no opening entry names the party.
 */
export function partyOpeningFromJournal(
  journals: LedgerJournal[], lines: LedgerLine[], kind: LedgerPartyKind, partyId: number | string, books?: LedgerBooks | null,
): { amount: number; date: string; journalId: number; ref: string } | null {
  // eslint-disable-next-line eqeqeq
  const opening = partyEntries(journals, lines, kind, partyId, books).filter(e => e.journal.is_opening == 1);
  if (!opening.length) return null;
  const last = opening[opening.length - 1].journal;
  const mine = opening.filter(e => Number(e.journal.journal_id) === Number(last.journal_id));
  return {
    amount: round2(mine.reduce((s, e) => s + e.amount, 0)),
    date: day(last.journal_date), journalId: Number(last.journal_id),
    ref: String(last.journal_number || `#${last.journal_id}`),
  };
}

/**
 * The party's ledger in one set of books: opening balance, then every later line with a
 * running balance. Lines dated before the opening balance were brought forward into it
 * and are left out, as are older opening entries.
 */
export function buildPartyLedger(o: {
  journals: LedgerJournal[]; lines: LedgerLine[];
  kind: LedgerPartyKind; partyId: number | string; books?: LedgerBooks | null;
  manualOpening?: number | string | null; manualOpeningDate?: string | null;
}): PartyLedger {
  const entries = partyEntries(o.journals, o.lines, o.kind, o.partyId, o.books);
  const fromJournal = partyOpeningFromJournal(o.journals, o.lines, o.kind, o.partyId, o.books);
  const manual = round2(num(o.manualOpening));
  const opening = fromJournal ? fromJournal.amount : manual;
  const openingDate = fromJournal ? fromJournal.date : day(o.manualOpeningDate);
  let running = opening;
  const rows: LedgerRow[] = entries
    // eslint-disable-next-line eqeqeq
    .filter(e => e.journal.is_opening != 1 && (!openingDate || day(e.journal.journal_date) >= openingDate))
    .map(e => {
      running = round2(running + e.amount);
      return {
        line_id: Number(e.line.line_id), journal_id: Number(e.journal.journal_id),
        date: day(e.journal.journal_date), ref: String(e.journal.journal_number || `#${e.journal.journal_id}`),
        memo: String(e.line.line_memo || e.journal.memo || ''),
        amount: e.amount, balance: running,
        reconcile_id: e.line.reconcile_id != null ? Number(e.line.reconcile_id) : null,
      };
    });
  return {
    opening, openingDate,
    openingFrom: fromJournal ? 'journal' : manual !== 0 ? 'manual' : 'none',
    openingRef: fromJournal ? fromJournal.ref : '',
    rows, balance: round2(running),
  };
}

export interface ReconcileSummary {
  cleared: number;        // opening + lines reconciled before + lines ticked now
  difference: number;     // statement − cleared; 0 = reconciled
  outstanding: number;    // book balance − cleared: lines not on the statement yet
  tickedCount: number;
}

/**
 * Where a reconciliation stands: the balance of everything ticked off (now or in an
 * earlier reconciliation) against what the party's statement says.
 */
export function reconcileSummary(ledger: PartyLedger, ticked: Set<number>, statementBalance: number): ReconcileSummary {
  let cleared = ledger.opening;
  let tickedCount = 0;
  ledger.rows.forEach(r => {
    if (r.reconcile_id != null) cleared += r.amount;
    else if (ticked.has(r.line_id)) { cleared += r.amount; tickedCount++; }
  });
  cleared = round2(cleared);
  return {
    cleared, tickedCount,
    difference: round2(num(statementBalance) - cleared),
    outstanding: round2(ledger.balance - cleared),
  };
}

/**
 * Opening balances typed in Masters that no journal entry carries — the amount the Trial
 * Balance adds to Accounts payable (vendors) / Accounts receivable (customers) so they
 * are not lost. A party whose opening balance came in through an opening-balances entry
 * is already in the ledger and is left out. Only parties of those books: a SHARED party
 * belongs to no single set of books, so its typed figure is added nowhere.
 * `asOf` (yyyy-mm-dd) leaves out balances dated later.
 */
export function manualOpeningTotal(o: {
  parties: Record<string, any>[]; pk: string; kind: LedgerPartyKind;
  journals: LedgerJournal[]; lines: LedgerLine[]; books: LedgerBooks; asOf?: string;
}): { total: number; count: number } {
  let total = 0, count = 0;
  o.parties.forEach(p => {
    // eslint-disable-next-line eqeqeq
    if (p.is_deleted == 1 || p.account_owner_id != o.books.account_owner_id || p.owner_type !== o.books.owner_type || p.owner_id != o.books.owner_id) return;
    const amount = round2(num(p.opening_balance));
    if (!amount) return;
    if (o.asOf && day(p.opening_balance_date) && day(p.opening_balance_date) > o.asOf) return;
    if (partyOpeningFromJournal(o.journals, o.lines, o.kind, p[o.pk], o.books)) return;
    total += amount; count++;
  });
  return { total: round2(total), count };
}

/** The books' control account for a kind of party, found by its name. */
export function findControlAccount<T extends { account_name?: string | null; is_header?: any }>(accounts: T[], kind: LedgerPartyKind): T | null {
  const re = kind === 'customer' ? /\baccounts?\s+receivable\b/i : /\baccounts?\s+payable\b/i;
  // eslint-disable-next-line eqeqeq
  return accounts.find(a => a.is_header != 1 && re.test(String(a.account_name || ''))) || null;
}
